// ═══════════════════════════════════════════════════════════════════════════
// useBranding — Lee la config de branding de Firestore (configuracion/branding)
// ═══════════════════════════════════════════════════════════════════════════
// Devuelve { nombre, subtitulo, colorPrimario, colorSecundario, loaded }
// Mientras carga o si no existe el doc, devuelve los defaults (Milbrands CoWork).
// Se lee UNA sola vez y queda en memoria (no listener en tiempo real).
// Además inyecta los colores como CSS custom properties en :root para que
// Tailwind y todo el sistema visual los use dinámicamente.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { BRANDING_DEFAULT } from '@core/lib/constants'

let _cache = null

// Permite invalidar el cache desde AdminPage al guardar branding
export function clearBrandingCache() { _cache = null }

// Convierte hex (#RRGGBB) a "R, G, B" para usar en rgba()
function hexToRgb(hex) {
  if (!hex) return null
  const h = hex.replace('#', '')
  if (h.length !== 6) return null
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null
  return `${r}, ${g}, ${b}`
}

// Genera un color hover (15% más oscuro)
function darken(hex, amount = 0.15) {
  if (!hex) return null
  const h = hex.replace('#', '')
  if (h.length !== 6) return null
  const r = Math.max(0, Math.round(parseInt(h.substring(0, 2), 16) * (1 - amount)))
  const g = Math.max(0, Math.round(parseInt(h.substring(2, 4), 16) * (1 - amount)))
  const b = Math.max(0, Math.round(parseInt(h.substring(4, 6), 16) * (1 - amount)))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Inyecta los colores de branding como CSS custom properties en :root
export function applyBrandingColors(colorPrimario, colorSecundario) {
  const root = document.documentElement
  if (colorPrimario) {
    root.style.setProperty('--brand-accent', colorPrimario)
    root.style.setProperty('--brand-accent-hover', darken(colorPrimario))
    root.style.setProperty('--brand-accent-rgb', hexToRgb(colorPrimario))
  }
  if (colorSecundario) {
    root.style.setProperty('--brand-purple', colorSecundario)
    root.style.setProperty('--brand-purple-rgb', hexToRgb(colorSecundario))
  }
}

export function useBranding() {
  const [branding, setBranding] = useState(_cache || { ...BRANDING_DEFAULT, loaded: !!_cache })

  useEffect(() => {
    if (_cache) {
      // Reaplicar colores por si se limpió el cache (ej. tras guardar en Admin)
      applyBrandingColors(_cache.colorPrimario, _cache.colorSecundario)
      return
    }
    let cancelled = false

    getDoc(doc(db, 'configuracion', 'branding'))
      .then(snap => {
        if (cancelled) return
        const data = snap.exists() ? snap.data() : {}
        _cache = {
          nombre: data.nombre || BRANDING_DEFAULT.nombre,
          subtitulo: data.subtitulo || BRANDING_DEFAULT.subtitulo,
          nombreEquipo: data.nombreEquipo || BRANDING_DEFAULT.nombreEquipo,
          colorPrimario: data.colorPrimario || null,
          colorSecundario: data.colorSecundario || null,
          loaded: true,
        }
        setBranding(_cache)
        applyBrandingColors(_cache.colorPrimario, _cache.colorSecundario)
      })
      .catch(() => {
        if (cancelled) return
        _cache = { ...BRANDING_DEFAULT, loaded: true }
        setBranding(_cache)
      })

    return () => { cancelled = true }
  }, [])

  return branding
}
